"use client";

import { useState } from "react";
import {
  Vote,
  Users,
  MapPin,
  Calendar,
  Award,
  ChevronDown,
  ChevronUp,
  Zap,
} from "lucide-react";
import { ContextAnalysis, PoliticsData } from "@/hooks/useAIContextAnalysis";

interface Market {
  id: string;
  question: string;
  outcomes: string[];
  outcomePrices: number[];
}

interface PoliticsAISidebarProps {
  analysis: ContextAnalysis;
  eventTitle: string;
  marketQuestions?: string[];
  markets?: Market[];
}

function getPartyColor(partyName: string, parties?: PoliticsData["parties"]): string {
  if (parties) {
    const party = parties.find(
      (p) =>
        p.name.toLowerCase() === partyName.toLowerCase() ||
        p.fullName.toLowerCase() === partyName.toLowerCase()
    );
    if (party) return party.color;
  }
  return "#6B7280";
}

function getPartyColorClasses(color: string) {
  return {
    bg: `bg-[${color}]/20`,
    border: `border-[${color}]/50`,
    text: `text-[${color}]`,
    solid: color,
  };
}

function getCandidatePrice(
  candidateName: string,
  markets?: Market[]
): number | null {
  if (!markets) return null;
  for (const market of markets) {
    const outcomeIndex = market.outcomes.findIndex(
      (o) => o.toLowerCase() === candidateName.toLowerCase()
    );
    if (outcomeIndex !== -1 && market.outcomePrices[outcomeIndex] != null) {
      return market.outcomePrices[outcomeIndex];
    }
    if (
      market.question.toLowerCase().includes(candidateName.toLowerCase()) &&
      market.outcomePrices[0] != null
    ) {
      return market.outcomePrices[0];
    }
  }
  return null;
}

export default function PoliticsAISidebar({
  analysis,
  eventTitle,
  marketQuestions = [],
  markets = [],
}: PoliticsAISidebarProps) {
  const [showAllCandidates, setShowAllCandidates] = useState(false);
  const [showReasoning, setShowReasoning] = useState(false);

  const politicsData = analysis.politicsData;

  if (!politicsData || !politicsData.country) {
    return null;
  }

  const candidates = analysis.entities.filter((e) => e.type === "candidate");
  const displayCandidates = showAllCandidates
    ? candidates
    : candidates.slice(0, 8);
  const hasMoreCandidates = candidates.length > 8;

  const primaryParties = (politicsData.parties || []).slice(0, 2);
  const gradientColors =
    primaryParties.length >= 2
      ? `from-[${primaryParties[0].color}] via-purple-500 to-[${primaryParties[1].color}]`
      : "from-purple-500 via-purple-600 to-blue-500";

  const flagUrl = `https://flagcdn.com/w80/${politicsData.countryCode.toLowerCase()}.png`;

  return (
    <div className="space-y-4">
      {/* Election Header */}
      <div className="bg-[#18181D] rounded-[5px] border border-purple-500/30 overflow-hidden">
        <div
          className={`bg-gradient-to-r ${gradientColors} px-4 py-3`}
          style={
            primaryParties.length >= 2
              ? {
                  background: `linear-gradient(to right, ${primaryParties[0].color}, #9333ea, ${primaryParties[1].color})`,
                }
              : undefined
          }
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img
                src={flagUrl}
                alt={politicsData.country}
                className="w-6 h-4 rounded-sm object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
              <Vote className="w-4 h-4 text-white" />
              <span className="text-white text-sm font-inter-tight font-bold truncate">
                {politicsData.electionName}
              </span>
            </div>
          </div>
        </div>

        <div className="p-4">
          {/* Win Condition */}
          {politicsData.winCondition && (
            <div className="flex items-center justify-between mb-3">
              <span className="text-gray-400 text-xs font-inter-tight">
                Win condition
              </span>
              <span className="text-white text-xs font-inter-tight font-bold">
                {politicsData.winCondition}
              </span>
            </div>
          )}

          {/* Electoral System */}
          <div className="flex items-center justify-between mb-3">
            <span className="text-gray-400 text-xs font-inter-tight">
              System
            </span>
            <span className="text-purple-400 text-xs font-inter-tight font-bold capitalize">
              {politicsData.electoralSystem}
            </span>
          </div>

          {/* Party Summary Bar */}
          {primaryParties.length >= 2 && (
            <div className="mt-3">
              <div className="flex items-center justify-between mb-1">
                <span
                  className="text-xs font-inter-tight font-bold"
                  style={{ color: primaryParties[0].color }}
                >
                  {primaryParties[0].name}
                </span>
                <span
                  className="text-xs font-inter-tight font-bold"
                  style={{ color: primaryParties[1].color }}
                >
                  {primaryParties[1].name}
                </span>
              </div>
              <div className="h-2 bg-[#1A1A1A] rounded-[5px] overflow-hidden flex">
                <div
                  className="h-full"
                  style={{
                    width: "48%",
                    backgroundColor: primaryParties[0].color,
                  }}
                />
                <div
                  className="h-full bg-purple-500"
                  style={{ width: "4%" }}
                />
                <div
                  className="h-full"
                  style={{
                    width: "48%",
                    backgroundColor: primaryParties[1].color,
                  }}
                />
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-[10px] text-gray-500 font-inter-tight">
                  {primaryParties[0].fullName}
                </span>
                <span className="text-[10px] text-gray-500 font-inter-tight">
                  {primaryParties[1].fullName}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Candidates */}
      {candidates.length > 0 && (
        <div className="bg-[#18181D] border-2 border-[#1A1A1A]/50 rounded-[5px] overflow-hidden hover:border-purple-500/40 transition-colors">
          <div className="bg-[#18181D] border-b border-[#1A1A1A]/50 px-4 py-3">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-purple-400" />
              <h3 className="text-white font-inter-tight font-bold text-sm uppercase tracking-wider">
                Candidates
              </h3>
              <span className="text-gray-500 text-xs font-inter-tight">
                ({candidates.length})
              </span>
            </div>
          </div>
          <div className="p-3 space-y-2 max-h-[300px] overflow-y-auto scrollbar-hide">
            {displayCandidates.map((candidate, idx) => {
              const partyName =
                candidate.metadata?.party || candidate.metadata?.partyColor
                  ? candidate.metadata?.party || ""
                  : "";
              const partyColor = candidate.metadata?.partyColor ||
                getPartyColor(partyName, politicsData.parties);
              const price = getCandidatePrice(candidate.name, markets);

              return (
                <div
                  key={idx}
                  className="flex items-center gap-3 p-2.5 rounded-[5px] bg-[#0f0f12]/50 border border-[#1A1A1A]/50 hover:border-purple-500/30 transition-colors"
                >
                  <div
                    className="w-8 h-8 rounded-[5px] flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                    style={{ backgroundColor: partyColor }}
                  >
                    {candidate.name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-satoshi font-bold text-sm truncate">
                      {candidate.name}
                    </p>
                    {partyName && (
                      <p
                        className="text-xs"
                        style={{ color: partyColor }}
                      >
                        {partyName}
                      </p>
                    )}
                  </div>
                  {price !== null && (
                    <span className="text-white font-monument font-bold text-sm">
                      {Math.round(price * 100)}%
                    </span>
                  )}
                </div>
              );
            })}

            {hasMoreCandidates && (
              <button
                onClick={() => setShowAllCandidates(!showAllCandidates)}
                className="w-full flex items-center justify-center gap-1 py-2 text-gray-400 hover:text-white text-xs font-inter-tight transition-colors"
              >
                {showAllCandidates ? (
                  <>
                    <ChevronUp className="w-4 h-4" />
                    Show less
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4" />
                    Show {candidates.length - 8} more
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Key Regions */}
      {politicsData.keyRegions && politicsData.keyRegions.length > 0 && (
        <div className="bg-[#18181D] border-2 border-[#1A1A1A]/50 rounded-[5px] overflow-hidden hover:border-purple-500/40 transition-colors">
          <div className="bg-[#18181D] border-b border-[#1A1A1A]/50 px-4 py-3">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-purple-400" />
              <h3 className="text-white font-inter-tight font-bold text-sm uppercase tracking-wider">
                Key Regions
              </h3>
            </div>
          </div>
          <div className="p-3 space-y-2">
            {politicsData.keyRegions.map((region, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between bg-[#0f0f12]/50 rounded-[5px] p-2.5 hover:bg-[#0f0f12] transition-colors"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-white font-satoshi font-bold text-sm truncate">
                    {region.name}
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {region.description && (
                    <span className="text-gray-400 text-xs font-inter-tight max-w-[120px] truncate">
                      {region.description}
                    </span>
                  )}
                  <span
                    className={`px-2 py-0.5 rounded text-[10px] font-inter-tight font-bold text-white ${
                      region.importance === "high"
                        ? "bg-red-500"
                        : region.importance === "medium"
                          ? "bg-yellow-500"
                          : "bg-gray-500"
                    }`}
                  >
                    {region.importance.toUpperCase()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Timeline */}
      {politicsData.timeline && politicsData.timeline.length > 0 && (
        <div className="bg-[#18181D] border-2 border-purple-500/20 rounded-[5px] p-4">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="w-4 h-4 text-purple-400" />
            <h3 className="text-white font-inter-tight font-bold text-sm">
              Key Dates
            </h3>
          </div>
          <div className="space-y-2 text-xs">
            {politicsData.timeline.map((item, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between"
              >
                <span
                  className={`font-satoshi ${item.isPast ? "text-gray-500 line-through" : "text-gray-400"}`}
                >
                  {item.event}
                </span>
                <span
                  className={`font-inter-tight font-bold ${item.isPast ? "text-gray-500" : "text-purple-400"}`}
                >
                  {item.date}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Historical Note */}
      {politicsData.historicalNote && (
        <div className="bg-[#0f0f12]/50 border border-[#1A1A1A] rounded-[5px] p-3">
          <div className="flex items-start gap-2">
            <Award className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
            <p className="text-gray-400 text-[10px] font-inter-tight leading-relaxed">
              {politicsData.historicalNote}
            </p>
          </div>
        </div>
      )}

      {/* AI Reasoning (collapsible) */}
      {analysis.reasoning && (
        <details className="group">
          <summary className="flex items-center gap-2 text-gray-500 text-xs font-inter-tight cursor-pointer hover:text-gray-300 transition-colors">
            <Zap className="w-3 h-3" />
            <span>Why this widget?</span>
          </summary>
          <p className="mt-2 text-gray-400 text-xs leading-relaxed pl-5">
            {analysis.reasoning}
          </p>
        </details>
      )}
    </div>
  );
}
